import { Link, useLocation } from "wouter";
import { ArrowLeft, Shield } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  getGroupedSections,
  sectionHref,
  type AdminSection,
} from "@/lib/admin-sections";

function isSectionActive(currentPath: string, section: AdminSection): boolean {
  const target = sectionHref(section);
  // Exact match, or current path is a descendant (e.g. /admin/stations/detail/123).
  return currentPath === target || currentPath.startsWith(`${target}/`);
}

export default function AdminSidebar() {
  const [location] = useLocation();
  const grouped = getGroupedSections();

  return (
    <aside className="w-80 bg-white border-r border-gray-200 min-h-screen">
      <div className="p-6">
        {/* Back-to-app exit */}
        <Link
          href="/dashboard"
          className="flex items-center space-x-2 px-3 py-2 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-50 hover:text-gray-900 transition-colors"
          data-testid="admin-nav-back-to-app"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Back to App</span>
        </Link>

        {/* Section header */}
        <div className="flex items-center space-x-3 mt-6 mb-4 px-3">
          <div className="p-2 bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg">
            <Shield className="h-4 w-4 text-white" />
          </div>
          <h2 className="text-sm font-semibold text-gray-900">Admin Settings</h2>
        </div>

        {/* Grouped section list */}
        <nav className="space-y-6">
          {grouped.map(({ group, sections }) => (
            <div key={group}>
              <div className="text-xs font-semibold uppercase tracking-wide text-gray-400 px-3 mb-2">
                {group}
              </div>
              <div className="space-y-1">
                {sections.map(section => {
                  const active = isSectionActive(location, section);
                  const Icon = section.icon;
                  return (
                    <Link
                      key={section.slug}
                      href={sectionHref(section)}
                      className={cn(
                        "flex items-center space-x-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors",
                        active
                          ? "bg-primary-50 text-primary-700"
                          : "text-gray-700 hover:bg-gray-50",
                      )}
                      data-testid={`admin-nav-${section.slug}`}
                    >
                      <Icon className="w-4 h-4 shrink-0" />
                      <span className="truncate">{section.label}</span>
                    </Link>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>
      </div>
    </aside>
  );
}
